// FILE: src/components/app/SearchBaseComponent/SearchResults.jsx
import React, { useEffect, useMemo, useState } from "react";
import TransactionsTableComponent from "../../TransactionsBaseComponent/TransactionsTableComponent/TransactionsTableComponent";
import Spinner from "../../../../common/Spinner/Spinner";
import styles from "./SearchResults.module.css";
import { searchUserTransactionsByDescriptionPrefix } from "../../../../../utils/firebase/features/transactions/fetchers";
import { normalizeFullPath } from "../../UploadDataComponent/utils/tagUtils";
import { getUserTransactionsCached } from "../../../../../utils/firebase/cache/transactionsCached";
import { TransactionsAPI } from "../../../../../utils/firebase";

/* ---------- config (match base defaults) ---------- */
const FULL_DATA_CAP = 9999;
const TX_TTL_MS = (() => {
  const v = Number(process.env.REACT_APP_TX_CACHE_TTL_MS);
  return Number.isFinite(v) && v > 0 ? v : 60 * 60 * 1000; // 1h
})();

/* ---------- helpers: tag normalization (same as TransactionsBase) ---------- */
function coerceTagArray(val) {
  if (Array.isArray(val)) {
    return Array.from(new Set(val.map((s) => normalizeFullPath(String(s || ""))).filter(Boolean)));
  }
  if (typeof val === "string") {
    return Array.from(
      new Set(
        val
          .split(/[|;,]/g)
          .map((s) => normalizeFullPath(String(s || "").trim()))
          .filter(Boolean)
      )
    );
  }
  return [];
}
function normalizeRowTags(row) {
  if (!row || typeof row !== "object") return row;
  const tagPath = coerceTagArray(row.tagPath ?? row.tagPaths);
  const next = { ...row };
  if (tagPath.length) next.tagPath = tagPath;
  delete next.tagPaths;
  return next;
}

/* ---------- helpers: sort + columns ---------- */
// date desc → updated desc → created desc → id desc
function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") { try { return v.toMillis(); } catch { return 0; } }
  if (v instanceof Date) return v.getTime();
  const p = Date.parse(v);
  return Number.isFinite(p) ? p : 0;
}
function sortRows(rows) {
  const copy = Array.isArray(rows) ? rows.slice() : [];
  copy.sort((a, b) => {
    const ad = Date.parse(a?.date) || 0;
    const bd = Date.parse(b?.date) || 0;
    if (ad !== bd) return bd - ad;
    const au = toMillis(a?.updatedAt);
    const bu = toMillis(b?.updatedAt);
    if (au !== bu) return bu - au;
    const ac = toMillis(a?.createdAt);
    const bc = toMillis(b?.createdAt);
    if (ac !== bc) return bc - ac;
    return String(b?.id || "").localeCompare(String(a?.id || ""));
  });
  return copy;
}
function inferColumns(items) {
  const cols = [];
  const seen = new Set();
  for (const row of items || []) {
    if (row && typeof row === "object") {
      for (const k of Object.keys(row)) {
        const key = k === "tagPaths" ? "tagPath" : k; // alias legacy → canonical
        if (!seen.has(key)) { seen.add(key); cols.push(key); }
      }
    }
  }
  return cols;
}
const aliasKey = (k) => (k === "tagPaths" ? "tagPath" : k);

/* ======================================================================= */

export default function SearchResults({ user, query, submitted }) {
  const email = String(user?.email || "").trim();

  const [state, setState] = useState({ loading: false, error: null, rows: [], source: null });
  const [selectedColumns, setSelectedColumns] = useState([]); // user's saved selection

  /* ---- load user's saved column selection (same source as TransactionsBase) ---- */
  useEffect(() => {
    let aborted = false;
    (async () => {
      if (!user?.uid) { setSelectedColumns([]); return; }
      try {
        const cfg = await TransactionsAPI.readUserTransactionsConfig({ user });
        const raw = (cfg?.exists && Array.isArray(cfg?.data?.selectedColumns)) ? cfg.data.selectedColumns : [];
        const aliased = raw.map(aliasKey);
        if (!aborted) setSelectedColumns(aliased);
      } catch {
        if (!aborted) setSelectedColumns([]);
      }
    })();
    return () => { aborted = true; };
  }, [user]);

  /* ---- perform search: cache-first, DB fallback ---- */
  useEffect(() => {
    let aborted = false;

    async function run() {
      const q = String(query || "").trim().toLowerCase();
      if (!email || !q) {
        setState({ loading: false, error: null, rows: [], source: null });
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null, source: null }));

      try {
        // 1) Local cache (full set; substring)
        let cachedRows = [];
        try {
          const res = await getUserTransactionsCached({
            userEmail: email,
            latestFirst: true,
            cap: FULL_DATA_CAP,
            ttlMs: TX_TTL_MS,
          });
          cachedRows = Array.isArray(res?.rows) ? res.rows : [];
        } catch { /* ignore */ }

        const normalized = cachedRows.map(normalizeRowTags);
        const localMatches = normalized.filter((r) =>
          String(r?.description || "").toLowerCase().includes(q)
        );

        if (!aborted && localMatches.length > 0) {
          setState({
            loading: false,
            error: null,
            rows: sortRows(localMatches),
            source: "cache",
          });
          return;
        }

        // 2) DB prefix search
        const dbRows = await searchUserTransactionsByDescriptionPrefix({
          userEmail: email,
          text: q,
          cap: 250,
          latestFirst: true,
        });
        if (!aborted) {
          const normalizedDb = (dbRows || []).map(normalizeRowTags);
          setState({
            loading: false,
            error: null,
            rows: sortRows(normalizedDb),
            source: "db",
          });
        }
      } catch (e) {
        if (!aborted) setState({ loading: false, error: e, rows: [], source: null });
      }
    }

    run();
    return () => { aborted = true; };
  }, [email, query]);

  /* ---- choose columns: intersect saved selection with what's present in results ---- */
  const columnsForResults = useMemo(() => {
    if (!state.rows?.length) return selectedColumns.length ? selectedColumns : undefined;
    const present = new Set(inferColumns(state.rows));
    const picked = (selectedColumns || []).filter((k) => present.has(k));
    return picked.length ? picked : undefined; // fallback to table inference if intersection empty
  }, [state.rows, selectedColumns]);

  const tableEmptyMessage = useMemo(() => {
    if (!submitted) return "Type a phrase and press Enter to search descriptions.";
    if (state.loading) return "";
    if (state.error) return "Search failed. Please try again.";
    return `No results for “${submitted}”.`;
  }, [submitted, state.loading, state.error]);

  const getRowId = (row, idx) => row?.id ?? `${row?.date}-${idx}`;

  return (
    <div className={styles.wrap}>
      {state.loading && (
        <div className="py-5 d-flex justify-content-center">
          <Spinner label="Searching…" />
        </div>
      )}

      {!state.loading && state.error && (
        <div className="alert alert-danger mt-3">
          <strong>Search failed.</strong>
          <div className="small mt-1">{String(state.error?.message || state.error)}</div>
        </div>
      )}

      {!state.loading && !state.error && (
        <>
          {state.source && (
            <div className="small text-secondary mb-2">
              Showing {state.rows.length} {state.rows.length === 1 ? "result" : "results"} from <strong>{state.source}</strong>.
            </div>
          )}
          <TransactionsTableComponent
            items={state.rows}
            columns={columnsForResults}   
            density="compact"
            stickyFirst
            striped
            rowsPerPageOptions={[25, 50, 100, 250]}
            initialRowsPerPage={25}
            selectable={false}
            getRowId={getRowId}
            emptyMessage={tableEmptyMessage}
          />
        </>
      )}
    </div>
  );
}